import {
    type BaseUser,
    getAoiRecordFromUserProgramState,
    getUserProgramState,
    getRelevantUserProgramStates,
    getUserProgramFieldState,
} from 'Users';
import { aoiRecordCreationTime, isAdmissionOffer, isProgramApplication } from 'ProgramAoi';
import { fetchBrandConfig, Brand } from 'AppBranding';
import { getProgramTypeConfig } from 'Program';
import { ProgramApplicationStatus } from 'ProgramApplication';
import { AdmissionOfferStatus } from 'AdmissionOffer';
import { type ProgramSwitcherProgramConfig } from './ProgramSwitcher.types';

// Checks if an ExecEd cert has a rejected application or declined offer. WillNotGraduate is intentionally
// excluded — those students were admitted and may still want to access their program content.
function isRejectedOrDeclinedExecEdCert(config: ProgramSwitcherProgramConfig, programType: string): boolean {
    if (!getProgramTypeConfig(programType)?.isExecEd) return false;

    const { aoiRecord } = config;
    if (!aoiRecord) return false;

    if (isProgramApplication(aoiRecord)) {
        return aoiRecord.status === ProgramApplicationStatus.NotSelectedForAdmission;
    }

    if (isAdmissionOffer(aoiRecord)) {
        return aoiRecord.status === AdmissionOfferStatus.DeclinedAdmissionOffer;
    }

    return false;
}

// oxlint-disable-next-line max-lines-per-function
export function getProgramSwitcherConfigs(user: BaseUser) {
    const programSwitcherConfigs: ProgramSwitcherProgramConfig[] = [];
    const userProgramStates = getRelevantUserProgramStates(user) ?? [];

    userProgramStates.forEach(userProgramState => {
        const programTypeConfig = getProgramTypeConfig(userProgramState.programType);
        const brand = programTypeConfig?.branding ?? Brand.quantic;
        const brandConfig = fetchBrandConfig(brand);
        const QuanticBrandConfig = fetchBrandConfig(Brand.quantic);
        const userProgramFieldState = getUserProgramFieldState(user, { userProgramState });

        // In the case where certain `brandConfig` values are undefined for a particular program type,
        // we can use Quantic branding as a fallback.
        const aoiRecord = getAoiRecordFromUserProgramState(user, userProgramState)!;

        const programSwitcherConfig: ProgramSwitcherProgramConfig = {
            aoiRecord,
            styleClass: brandConfig.brandStyleClass ?? QuanticBrandConfig.brandStyleClass,
            programTitle: programTypeConfig?.programSwitcherMenuTitle(userProgramState.cohort) ?? '',
            menuLogo: (brandConfig.appMenuProgramLogo ?? QuanticBrandConfig.appMenuProgramLogo) as string,
            institutionId: brandConfig.institutionId ?? QuanticBrandConfig.institutionId,
            userProgramFieldStateId: userProgramFieldState!.id,
        };
        const isPrefProgram = userProgramFieldState?.id === user.prefUserProgramFieldStateId;
        programSwitcherConfig.hiddenByDefault =
            !isPrefProgram && isRejectedOrDeclinedExecEdCert(programSwitcherConfig, userProgramState.programType);

        programSwitcherConfigs.push(programSwitcherConfig);
    });

    if (programSwitcherConfigs.length === 0) return [];

    const programTypeForLastActiveProgram = getUserProgramState(user)?.programType;
    if (!programTypeForLastActiveProgram) return [];
    const brandForLastActiveProgram = getProgramTypeConfig(programTypeForLastActiveProgram)?.branding ?? Brand.quantic;
    const brandConfigForLastActiveProgram = fetchBrandConfig(brandForLastActiveProgram);

    // Sort programs by:
    // 1. Non-hidden programs before hidden-by-default programs
    // 2. Pref program
    // 3. Other programs in the same institution, recency descending, programs without an AOI record last
    // 4. Other programs NOT in the same institution, recency descending, programs without an AOI record last
    // oxlint-disable-next-line complexity
    programSwitcherConfigs.sort((a, b) => {
        if (a.hiddenByDefault && !b.hiddenByDefault) return 1;
        if (!a.hiddenByDefault && b.hiddenByDefault) return -1;

        if (
            a.userProgramFieldStateId === user.prefUserProgramFieldStateId &&
            b.userProgramFieldStateId !== user.prefUserProgramFieldStateId
        ) {
            return -1;
        }

        if (
            a.userProgramFieldStateId !== user.prefUserProgramFieldStateId &&
            b.userProgramFieldStateId === user.prefUserProgramFieldStateId
        ) {
            return 1;
        }

        if (
            a.institutionId === brandConfigForLastActiveProgram.institutionId &&
            b.institutionId !== brandConfigForLastActiveProgram.institutionId
        ) {
            return -1;
        }

        if (
            a.institutionId !== brandConfigForLastActiveProgram.institutionId &&
            b.institutionId === brandConfigForLastActiveProgram.institutionId
        ) {
            return 1;
        }

        if (a.aoiRecord && !b.aoiRecord) {
            return -1;
        }

        if (!a.aoiRecord && b.aoiRecord) {
            return 1;
        }

        if (a.aoiRecord && b.aoiRecord) {
            if (aoiRecordCreationTime(a.aoiRecord) > aoiRecordCreationTime(b.aoiRecord)) return -1;
            if (aoiRecordCreationTime(a.aoiRecord) < aoiRecordCreationTime(b.aoiRecord)) return 1;
        }

        return 0;
    });

    // First item in the array is the most recent, active program. Set property to `selected`
    // in order to apply various css styles.
    programSwitcherConfigs[0].selected = true;

    return programSwitcherConfigs;
}
